/**
 * Structured content for the JbnWorks service catalog site.
 * Mirrors the published catalog; company-framed (no personal attribution).
 */

export const HERO_STATS = [
  { num: '15 Yrs', lbl: 'Cloud & Architecture Practice' },
  { num: '30+', lbl: 'Public-Sector Rollouts' },
  { num: '20+', lbl: 'Enterprise & SME Rollouts' },
  { num: '20+', lbl: 'Active Certifications' },
];

export const CIRCUIT_VIEWBOX = '-110 -30 700 520';
const CX = 240;
const CY = 235;
export const CIRCUIT_CENTER = { cx: CX, cy: CY };
export const CIRCUIT_NODES = [
  { cx: 240, cy: 75, label: 'Application', sub: '01 · BUILD', anchor: 'middle', lx: 240, ly: 33 },
  { cx: 353, cy: 122, label: 'Data', sub: '02 · GOVERN', anchor: 'middle', lx: 353, ly: 80 },
  { cx: 400, cy: 235, label: 'Infrastructure', sub: '03 · CLOUD', anchor: 'start', lx: 440, ly: 239 },
  { cx: 353, cy: 348, label: 'Automation', sub: '04 · IaC', anchor: 'middle', lx: 353, ly: 396 },
  { cx: 240, cy: 395, label: 'Zero Trust', sub: '05 · SECURE', anchor: 'middle', lx: 240, ly: 443 },
  { cx: 127, cy: 348, label: 'DevSecOps', sub: '06 · OPERATE', anchor: 'middle', lx: 127, ly: 396 },
  { cx: 80, cy: 235, label: 'Accounting', sub: '07 · ACCOUNT', anchor: 'end', lx: 40, ly: 239 },
  { cx: 127, cy: 122, label: 'FinOps', sub: '08 · OPTIMIZE', anchor: 'middle', lx: 127, ly: 80 },
];
export const CIRCUIT_TRACES = CIRCUIT_NODES.map((n) => `M${CX},${CY} L${n.cx},${n.cy}`);

// --- SERVICE PILLARS (accordion) ---
export const PILLARS = [
  {
    id: 'pillar-1',
    num: '01',
    icon: 'app',
    title: 'Application Modernization',
    tagline: 'Legacy re-platforming to cloud-native, containerized applications — any technology stack',
    deliver: [
      'Legacy-to-cloud-native re-platforming and re-architecture',
      'Monolith decomposition into microservices and API-driven architectures',
      'Containerization and orchestration on AKS / EKS / GKE',
      'Full-stack development and modernization across Java, Python, Node.js, and .NET/React stacks — matched to the technology already in place or the target architecture',
    ],
    outcome: 'faster release cycles, reduced technical debt, and applications that are portable across clouds.',
    chips: ['Java / Spring', 'Python', 'Node.js', 'React', '.NET Core', 'Angular', 'Docker', 'Kubernetes', 'CKAD', 'REST APIs', 'Cloud-Native Design'],
  },
  {
    id: 'pillar-2',
    num: '02',
    icon: 'data',
    title: 'Data Modernization',
    tagline: 'Governed, AI-ready data platforms and analytics estates',
    deliver: [
      'Data platform re-architecture and lakehouse modernization',
      'Data governance, cataloging, and lineage',
      'Streaming, CDC, and integration pipelines',
      'Analytics and reporting layers ready for AI/ML workloads',
    ],
    outcome: 'trusted, governed data that is ready for analytics and AI at enterprise scale.',
    chips: ['Azure Synapse', 'Databricks', 'Microsoft Fabric', 'Purview', 'Data Factory', 'Streaming / CDC'],
  },
  {
    id: 'pillar-3',
    num: '03',
    icon: 'grid',
    title: 'Infrastructure Modernization & Cloud-Native Development',
    tagline: 'Landing zones and cloud-native builds across Azure, AWS and GCP',
    deliver: [
      'Multi-cloud landing zones aligned to Cloud Adoption Framework',
      'Workload migration, re-hosting, and cloud-native rebuild',
      'Networking, resilience, and cost-optimized compute design',
      'Cloud-native application development on managed platform services',
    ],
    outcome: 'a resilient, cost-optimized, cloud-native foundation — the right platform for each workload.',
    chips: ['Azure Landing Zones', 'AWS VPC / TGW', 'GCP Data Fusion', 'AKS / App Services', 'ExpressRoute'],
  },
  {
    id: 'pillar-4',
    num: '04',
    icon: 'gear',
    title: 'End-to-End Automation & Infrastructure-as-Code',
    tagline: 'Every environment provisioned, configured and deployed as code',
    deliver: [
      'Infrastructure-as-Code across compute, network, and platform layers',
      'Configuration management and golden-image pipelines',
      'Native cloud CLI & SDK automation for Azure, AWS, and GCP',
      'Policy-driven, repeatable environment provisioning',
    ],
    outcome: 'repeatable, drift-free environments — every change traceable to a commit, not a console click.',
    chips: ['Terraform', 'Ansible', 'ARM / Bicep', 'Azure CLI', 'AWS CLI', 'gcloud CLI', 'Helm'],
  },
  {
    id: 'pillar-5',
    num: '05',
    icon: 'shield',
    title: 'Zero Trust Security & Zero-Touch Deployment',
    tagline: 'Identity-first security with no manual access to production',
    deliver: [
      'Zero Trust architecture across identity, network, workload, and data',
      'Policy-as-code and compliance guardrails embedded in pipelines',
      'Zero-touch deployment — every change ships through pipeline and code review, never console access',
      'SAST/DAST scanning and secrets management on every release',
    ],
    outcome: 'compliant, auditable delivery that removes manual, human-error-prone production access.',
    chips: ['Zero Trust', 'Defender for Cloud', 'Key Vault', 'Azure Firewall / WAF', 'SAST / DAST'],
  },
  {
    id: 'pillar-6',
    num: '06',
    icon: 'flow',
    title: 'CI/CD, DevOps, SecOps & DataOps',
    tagline: 'One automated path to production across every domain',
    deliver: [
      'Unified CI/CD pipeline strategy spanning application, infrastructure, security, and data',
      'DevSecOps gates and approval workflows built into every pipeline',
      'DataOps pipelines for analytics and ML delivery',
      'Observability and release governance across every environment',
    ],
    outcome: 'a single, governed automation backbone instead of separate tools per team.',
    chips: ['Azure DevOps', 'GitHub Actions', 'Jenkins', 'Grafana', 'Kibana', 'DataDog', 'Prometheus'],
  },
  {
    id: 'pillar-7',
    num: '07',
    icon: 'ledger',
    title: 'Auditing & Accounting',
    tagline: 'Financial visibility and control for IT and non-IT operations alike',
    deliver: [
      'Statutory, management and cost accounting for both technology and non-technology organizations',
      'Independent audit and assurance over financial records, internal controls and governance',
      'Cost accounting and chargeback / showback models that make technology spend visible and attributable',
      'Budgeting, forecasting and variance analysis that keep programs on plan',
    ],
    outcome:
      'an IT landscape that is predictable from a costing perspective — without auditing and accounting, technology spend stays volatile, unattributed and hard to defend; with it, every cost is measured, owned and audit-ready.',
    chips: ['IFRS / GAAP', 'Internal & External Audit', 'Cost Accounting', 'Chargeback / Showback', 'Budget & Forecast', 'Financial Controls'],
  },
  {
    id: 'pillar-8',
    num: '08',
    icon: 'gauge',
    title: 'FinOps & Operations Monitoring',
    tagline: 'Cloud cost accountability and always-on operational visibility',
    deliver: [
      'FinOps operating model — cost allocation, unit economics and cross-team accountability for cloud spend',
      'Commitment planning, rightsizing and waste-reduction across Azure, AWS and GCP',
      'Observability and operations monitoring — metrics, logs, traces, dashboards and alerting',
      'SLO / SLA management, incident response and continuous optimization',
    ],
    outcome:
      'cloud spend that tracks value, and operations you can see — anomalies caught in a dashboard, not on the invoice.',
    chips: ['FinOps', 'Cost Allocation', 'Rightsizing', 'Azure Cost Management', 'Grafana', 'Prometheus', 'Azure Monitor', 'SLO / SLA'],
  },
];

// --- AUDIT, ASSURANCE & VENDOR GOVERNANCE ---
export const ASSURANCE_AUDIT = [
  { icon: 'ledger', h: 'Internal Audit', p: 'Independent review of internal controls, governance, and risk-management processes across the organization, with practical remediation guidance' },
  { icon: 'building', h: 'External Audit', p: 'Independent third-party audit and attestation for regulatory, statutory, and stakeholder assurance, coordinated end to end' },
  { icon: 'coins', h: 'Financial Audit', p: 'Examination of financial statements, records, and controls for accuracy, completeness, and compliance with applicable standards' },
  { icon: 'search', h: 'IT Audit Services', p: 'Independent assessment of IT controls, processes, and infrastructure against policy and regulatory requirements' },
  { icon: 'code', h: 'Code Review Services', p: 'Structured peer and architectural review of application code for quality, maintainability, and standards compliance' },
  { icon: 'shield', h: 'Code Security Review', p: 'Security-focused review identifying vulnerabilities, insecure patterns, and hardening opportunities in source code' },
  { icon: 'vendors', h: 'Third-Party & Vendor Code Review', p: 'Independent review of code delivered by external vendors and contractors before acceptance into production' },
  { icon: 'lock', h: 'COTS & Vendor Production Security', p: 'Security assessment of commercial off-the-shelf software and vendor-managed production environments' },
  { icon: 'compare', h: 'Compatibility Assessment', p: 'Evaluation of software and system compatibility across platforms, versions, and integration points' },
  { icon: 'exit', h: 'Vendor Lock-In & Exit Strategy Review', p: 'Assessment of dependency on a given vendor or platform, with a practical exit and portability strategy' },
];

// Licensed finance, tax and business-advisory activities offered alongside audit.
export const ASSURANCE_BUSINESS = [
  { icon: 'book', h: 'Accounting & Bookkeeping', p: 'Ledger maintenance, reconciliations and periodic financial reporting that keep records accurate and audit-ready' },
  { icon: 'receipt', h: 'Tax Consultancy', p: 'Advisory on corporate tax, VAT and regulatory filings — structuring, compliance and end-to-end filing support' },
  { icon: 'gauge', h: 'Cost Control & Risk Management', p: 'Cost-control frameworks and enterprise risk management that identify, quantify and mitigate financial and operational risk' },
  { icon: 'cap', h: 'Accounting Training', p: 'Practical training for finance teams on accounting standards, internal controls and reporting tools' },
  { icon: 'chart', h: 'Business Analysis', p: 'Requirements discovery, process mapping and solution definition that turn business goals into delivery-ready specifications' },
  { icon: 'tm', h: 'Trademark Registration Agent', p: 'Trademark search, filing and prosecution as a registered agent, protecting brand and intellectual-property assets' },
  { icon: 'keyboard', h: 'Data Entry & Records Management', p: 'Accurate, controlled data capture and digitization with validation and quality checks' },
];

// --- CLOUD PLATFORM EXPERTISE (no percentage tile) ---
export const CLOUDS = {
  azure: {
    label: 'Azure',
    services: [
      'Landing Zones & CAF Governance', 'AKS & App Services',
      'Functions & Data Factory', 'Synapse & Purview',
      'ExpressRoute & VPN Gateway', 'Azure Firewall & WAF',
      'Key Vault & Defender for Cloud', 'Azure Monitor & Log Analytics (KQL)',
    ],
  },
  aws: {
    label: 'AWS',
    services: [
      'VPC & Transit Gateway', 'Lambda',
      'Multi-cloud & DR/BCDR strategy', 'Workload portability design',
      'Security & compliance tooling', 'Cost & capacity planning',
    ],
  },
  gcp: {
    label: 'Google Cloud',
    services: [
      'Data Fusion', 'IAM',
      'Analytics & data-platform workloads', 'Selective multi-cloud strategy engagements',
    ],
  },
};

// --- AUTOMATION TOOLBELT ---
export const TOOLS = [
  { name: 'Terraform', cat: 'IaC · Provisioning', back: 'Multi-cloud resource provisioning with a single declarative workflow and state-managed deployments.' },
  { name: 'Ansible', cat: 'Configuration', back: 'Agentless configuration management — consistent OS, middleware, and application configuration at scale.' },
  { name: 'ARM / Bicep', cat: 'Azure Native IaC', back: 'Native Azure infrastructure templates, integrated directly with Azure governance and policy.' },
  { name: 'Azure / AWS / gcloud CLI', cat: 'Native Automation', back: 'Scripted, native cloud CLI automation for tasks outside standard IaC coverage — no manual console steps.' },
  { name: 'Azure DevOps', cat: 'CI/CD', back: 'End-to-end pipeline orchestration for build, test, security scan, and release across environments.' },
  { name: 'GitHub Actions', cat: 'CI/CD', back: 'Git-native automation for build and release workflows, including advanced security scanning.' },
  { name: 'Jenkins', cat: 'CI/CD', back: 'Extensible pipeline orchestration for legacy and hybrid environments alongside cloud-native tooling.' },
  { name: 'Helm', cat: 'Kubernetes', back: 'Versioned, repeatable Kubernetes application packaging and release management.' },
];

// --- ZERO TRUST PILLARS ---
export const ZT_PILLARS = [
  { icon: 'identity', h: 'Identity', p: 'Least-privilege access, conditional policies, strong authentication' },
  { icon: 'network', h: 'Network', p: 'Micro-segmentation, private endpoints, firewall & WAF policy' },
  { icon: 'workload', h: 'Workload', p: 'Container and workload hardening, runtime protection' },
  { icon: 'data', h: 'Data', p: 'Encryption, key management, and data-access governance' },
  { icon: 'pipeline', h: 'Pipeline', p: 'Policy-as-code, SAST/DAST, and approval gates on every release' },
];

export const FLOW_STEPS = ['Code Commit', 'Automated Review', 'Security Gate', 'Pipeline Deploy', 'Observability'];

// --- ENGAGEMENT MODEL ---
export const STEPS = [
  {
    n: '01', title: 'Discover & Assess',
    activities: [
      'Discovery workshops and current-state assessment',
      'CAF-aligned, ADM-grounded roadmap definition',
      'Bid qualification, TCO/ROI modeling, and BoQ preparation',
    ],
    output: ['Architecture Vision & target-state roadmap', 'Business case with cost and risk baseline'],
  },
  {
    n: '02', title: 'Architect & Design',
    activities: [
      'HLD / LLD design across application, data, and infrastructure',
      'Reference architecture and governance model definition',
      'Architecture board review and sign-off',
    ],
    output: ['Approved architecture blueprint', 'Governance & compliance checkpoints (TOGAF ADM)'],
  },
  {
    n: '03', title: 'Automate & Build',
    activities: [
      'Infrastructure-as-Code build across environments',
      'CI/CD pipeline construction and containerized application build',
      'Data pipeline and integration development',
    ],
    output: ['Automated, repeatable environments', 'Working pipelines from commit to deploy'],
  },
  {
    n: '04', title: 'Secure & Validate',
    activities: [
      'Zero Trust control implementation and validation',
      'SAST/DAST scanning and policy-as-code enforcement',
      'Compliance and audit-readiness checks',
    ],
    output: ['Security-validated release', 'Compliance evidence and audit trail'],
  },
  {
    n: '05', title: 'Operate & Optimize',
    activities: [
      'Observability, alerting, and release governance',
      'FinOps and cost optimization review',
      'Continuous improvement and knowledge transfer',
    ],
    output: ['Operating dashboards and runbooks', 'Optimization roadmap for the next cycle'],
  },
];

// --- ENTERPRISE EXPERTISE / CERTIFICATIONS (company capability) ---
export const EXPERTISE_STATS = [
  { num: '20+', lbl: 'Active Certifications' },
  { num: '3', lbl: 'Hyperscalers Covered' },
  { num: '15', lbl: 'Years in Practice' },
  { num: '30+', lbl: 'Top-100 Regional Customers Served' },
];

export const CERTS = {
  ms: {
    label: 'Microsoft',
    swatch: 'sw-ms',
    items: [
      { code: 'AZ', name: 'Azure Solutions Architect Expert', issuer: 'Microsoft · Active' },
      { code: 'AZ', name: 'Azure DevOps Engineer Expert', issuer: 'Microsoft · Active' },
      { code: 'AZ', name: 'Azure Developer Associate', issuer: 'Microsoft · Active' },
      { code: 'AZ', name: 'Azure Administrator Associate', issuer: 'Microsoft · Active' },
      { code: 'AZ', name: 'Azure Virtual Desktop Specialty', issuer: 'Microsoft · Active' },
      { code: 'FB', name: 'Fabric Data Engineer Associate', issuer: 'Microsoft · Active' },
      { code: 'FB', name: 'Fabric Analytics Engineer Associate', issuer: 'Microsoft · Active' },
      { code: 'GH', name: 'GitHub Actions', issuer: 'GitHub · Active' },
      { code: 'GH', name: 'GitHub Advanced Security', issuer: 'GitHub · Active' },
      { code: 'MCT', name: 'Microsoft Certified Trainer', issuer: 'Microsoft · Active 2026' },
    ],
  },
  aws: {
    label: 'AWS',
    swatch: 'sw-aws',
    items: [
      { code: 'SA', name: 'Solutions Architect – Professional', issuer: 'AWS · Active' },
      { code: 'SA', name: 'Solutions Architect – Associate', issuer: 'AWS · Active' },
      { code: 'DO', name: 'DevOps Engineer – Professional', issuer: 'AWS · Active' },
      { code: 'SEC', name: 'Security – Specialty', issuer: 'AWS · Active' },
      { code: 'PTR', name: 'Partner: Technical Accredited', issuer: 'AWS · Active' },
      { code: 'PTR', name: 'Partner: Cloud Economics', issuer: 'AWS · Active' },
    ],
  },
  gcp: {
    label: 'Google Cloud',
    swatch: 'sw-gcp',
    items: [
      { code: 'PCA', name: 'Professional Cloud Architect', issuer: 'Google Cloud · Active' },
      { code: 'CDE', name: 'Professional Cloud DevOps Engineer', issuer: 'Google Cloud' },
    ],
  },
  gov: {
    label: 'Governance & Delivery',
    swatch: 'sw-gov',
    items: [
      { code: 'TG', name: 'TOGAF 9 Certified', issuer: 'The Open Group · Enterprise Architecture' },
      { code: 'PMP', name: 'Project Management Professional', issuer: 'PMI · Active' },
      { code: 'CKAD', name: 'Certified Kubernetes App Developer', issuer: 'Linux Foundation · Active' },
    ],
  },
};

export const NAV_SECTIONS = [
  { id: 'services', label: 'Services' },
  { id: 'assurance', label: 'Audit & Assurance' },
  { id: 'cloud', label: 'Cloud Expertise' },
  { id: 'automation', label: 'Automation' },
  { id: 'security', label: 'Security' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'expertise', label: 'Company Expertise' },
];
